import React, { Suspense, useEffect, useState } from "react";
import { Route, Routes, useLocation, matchPath } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.css";
import browseRoutes, { getRoleBasedRoutes } from "../routes";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProtectedRoute from "../components/ProtectedRoute";
import "../utils/style/style.scss";
import { Provider, useDispatch, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "../Redux_Toolkit/store";
// import MainLayout from './components/layouts/MainLayout'
// import { ToastContainer } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import { fetchConfigurations } from "../Redux_Toolkit/configuration/configurationThunk";
import { config as appConfig } from "../config/config";
import { initializeTokenRefresh } from "../utils/tokenRefresh";
import MerchantProfileModal from "../pages/Auth/ProfileCompletion/MerchantProfileModal";
import InfluencerProfileModal from "../pages/Auth/ProfileCompletion/InfluencerProfileModal";
import { getInfluencerProfile } from "../Redux_Toolkit/Influencer/InfluencerThunk";
import { getMerchantProfile } from "../Redux_Toolkit/Merchant/MerchantThunk";

const API_BASE = appConfig.localApiUrl;
const ASSET_BASE = API_BASE.replace(/\/api\/?$/, "");

function setFavicon(href) {
  let link =
    document.querySelector("link[rel='icon']") ||
    document.querySelector("link[rel='shortcut icon']");

  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }

  link.type = "image/png";
  link.href = href;
}

const getStoredAuthSnapshot = () => {
  const token = localStorage.getItem("token");
  const profileComplete = localStorage.getItem("profileComplete");
  const identifierType = sessionStorage.getItem("v2_identifier_type");
  const identifierValue = sessionStorage.getItem("v2_identifier");

  let user = null;
  try {
    const rawUser = localStorage.getItem("user");
    user = rawUser ? JSON.parse(rawUser) : null;
  } catch (error) {
    user = null;
  }

  return {
    token,
    user,
    profileComplete,
    identifierType,
    identifierValue,
  };
};

function AppContent() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [configData, setConfigData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [authSnapshot, setAuthSnapshot] = useState(getStoredAuthSnapshot);
  const [continueBanner, setContinueBanner] = useState(null);
  const authState = useSelector((state) => state?.auth);
  const user = authState?.loggedInUser?.data?.user;

  // Get role from Redux state or localStorage (for page refresh)
  const getUserRole = () => {
    // First try Redux state
    if (user?.role) {
      return user.role;
    }
    // Fallback to localStorage
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser?.role;
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }
    return null;
  };

  const userRole = getUserRole();

  // Get role-based routes
  const routes = getRoleBasedRoutes(userRole);

  const createRoutes = () => {
    const retData = routes.map((route, index) => {
      return route.element ? (
        <Route
          path={route.path}
          element={
            route.protected ? (
              <ProtectedRoute>{route.element}</ProtectedRoute>
            ) : (
              route.element
            )
          }
          exact={route.exact}
          key={`page-route-${index}`}
        />
      ) : null;
    });
    return retData;
  };

  // Get current route metadata from the most-specific match.
  // This avoids generic dynamic routes (e.g. /influencer/:id) shadowing
  // more specific paths (e.g. /influencer/create-exclusive-deal).
  const currentRoute = (() => {
    const matchedRoutes = routes
      .filter((route) => {
        if (!route?.path) return false;
        return !!matchPath(
          { path: route.path, end: route.exact !== false },
          location.pathname,
        );
      })
      .map((route) => {
        const path = route.path || "";
        const segments = path.split("/").filter(Boolean);
        const staticSegments = segments.filter(
          (segment) => !segment.startsWith(":") && segment !== "*",
        ).length;
        const dynamicSegments = segments.filter((segment) => segment.startsWith(":")).length;
        const wildcardPenalty = path.includes("*") ? 100 : 0;

        return {
          route,
          score: (staticSegments * 10) - dynamicSegments - wildcardPenalty + path.length,
        };
      })
      .sort((a, b) => b.score - a.score);

    return matchedRoutes[0]?.route;
  })();
  const showLayout = !currentRoute?.noLayout;
  const showFooter = showLayout && !currentRoute?.noFooter;

  useEffect(() => {
    setAuthSnapshot(getStoredAuthSnapshot());
  }, [
    location.pathname,
    authState?.loggedInUser?.status,
    authState?.completeMerchantProfile?.status,
    authState?.completeInfluencerProfile?.status,
  ]);

  useEffect(() => {
    const syncSnapshot = () => {
      setAuthSnapshot(getStoredAuthSnapshot());
    };

    window.addEventListener("user:updated", syncSnapshot);
    window.addEventListener("storage", syncSnapshot);

    return () => {
      window.removeEventListener("user:updated", syncSnapshot);
      window.removeEventListener("storage", syncSnapshot);
    };
  }, []);

  const profileRole = authSnapshot?.user?.role;
  const profilePending = authSnapshot?.profileComplete === "false";
  const isAuthenticatedUser = Boolean(authSnapshot?.token && authSnapshot?.user);
  const isHomeRoute =
    location.pathname === "/" ||
    (profileRole === "influencer" && location.pathname === "/influencer");
  const shouldShowProfileCompletionModal =
    isAuthenticatedUser && profilePending && isHomeRoute;

  const modalIdentifierType =
    authSnapshot?.identifierType ||
    (authSnapshot?.user?.email
      ? "email"
      : authSnapshot?.user?.phone
        ? "phone"
        : null);

  const modalContact =
    authSnapshot?.identifierValue ||
    (modalIdentifierType === "email"
      ? authSnapshot?.user?.email
      : authSnapshot?.user?.phone) ||
    "";

  const handleProfileModalComplete = async (role) => {
    if (role === "merchant") {
      await dispatch(getMerchantProfile());
    }

    if (role === "influencer") {
      await dispatch(getInfluencerProfile());
    }

    setAuthSnapshot(getStoredAuthSnapshot());
  };

  const getConfigData = async () => {
    try {
      const res = await fetch(
        `${appConfig.localApiUrl}/api/configurations/Configurations`
      );
      if (!res.ok) throw new Error("Network response was not ok");
      const data = await res.json();

      const cfg = data?.Result?.config ?? data?.data?.Result?.config ?? {};

      localStorage.setItem("config", JSON.stringify(cfg));
      setConfigData(cfg);
      setIsLoading(false);
    } catch (e) {
      // console.log(e);
    }
  };

  useEffect(() => {
    getConfigData();
  }, []);

  useEffect(() => {
    initializeTokenRefresh();
  }, []);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("continuedFromApp");
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!parsed?.at) return;

      const startedAt = new Date(parsed.at).getTime();
      if (Number.isNaN(startedAt)) return;

      // Show only for fresh handoffs.
      if (Date.now() - startedAt > 10 * 60 * 1000) {
        sessionStorage.removeItem("continuedFromApp");
        return;
      }

      setContinueBanner(parsed);
    } catch {
      // Ignore malformed storage payload.
    }
  }, [location.pathname]);

  // ADD THIS useEffect HERE (tab title + favicon)
  useEffect(() => {
    if (!configData || Object.keys(configData).length === 0) return;

    document.title = configData.Website_Name;

    if (configData.Favicon) {
      const faviconUrl = `${ASSET_BASE}${configData.Favicon}?v=${Date.now()}`;
      setFavicon(faviconUrl);
    }
  }, [configData]);


  return (
    <div className="App">
      {showLayout && <Header configData={configData} />}
      {showLayout && continueBanner ? (
        <div
          style={{
            background: "#E3F2FD",
            borderBottom: "1px solid #BBDEFB",
            color: "#0D47A1",
            padding: "8px 14px",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>
            Session continued from merchant app. You are now signed in on web as your normal account.
          </span>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem("continuedFromApp");
              setContinueBanner(null);
            }}
            style={{
              border: "none",
              background: "transparent",
              color: "#0D47A1",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {/* <MainLayout> */}
      <div style={{ flex: 1 }}>
        <Suspense fallback={null}>
          <Routes key={location.pathname}>{createRoutes()}</Routes>
          <ToastContainer
            enableMultiContainer
            containerId={"TOP_RIGHT"}
            hideProgressBar={true}
          />
        </Suspense>
      </div>

      <MerchantProfileModal
        isOpen={shouldShowProfileCompletionModal && profileRole === "merchant"}
        contact={modalContact}
        identifierType={modalIdentifierType}
        onComplete={() => handleProfileModalComplete("merchant")}
      />

      <InfluencerProfileModal
        isOpen={shouldShowProfileCompletionModal && profileRole === "influencer"}
        contact={modalContact}
        identifierType={modalIdentifierType}
        onComplete={() => handleProfileModalComplete("influencer")}
      />

      {showFooter && <Footer configData={configData} />}
      {/* </MainLayout> */}
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AppContent />
      </PersistGate>
    </Provider>
  );
}

export default App;
